import {getId, getType} from 'apis/model/base'
import getTitle from 'helpers/getTitle'
import {LoginContext} from 'components/LoginContext'
import classNames from 'classnames'
import useTranslate from 'modules/local/useTranslate'
import React, {useCallback, useContext, useEffect, useMemo, useRef, useState,} from 'react'
import {BiCheck, BiChevronDown, BiGlobe, BiLock, BiSearch, BiSolidChurch, BiX} from 'react-icons/bi'
import {RiSignalTowerFill, RiUserFill} from 'react-icons/ri'
import {PostFormContext} from './PostFormContext'
import {useFormikContext} from 'formik'
import {ChannelIcon} from "../../icons/ChannelIcon"
import DrawDialog from "../../drawer/dialog/DrawDialog";
import {BsChevronDown} from "react-icons/bs";

// ─── Constants ────────────────────────────────────────────────────────────────

export const PrivacyTypes = Object.freeze({
    PUBLIC:  1,
    PRIVATE: 2,
})

// ─── Helpers ──────────────────────────────────────────────────────────────────

const getLabel  = (item) => item?.title ?? item?.name ?? item?.username ?? item?.idname ?? ''
const getAvatar = (item) => item?.avatar ?? item?.photo ?? null

/**
 * Resolve creator options from a channel option.
 * Falls back to login user if creators array is empty.
 */
const resolveCreatorOptions = (channelOption, login) => {
    const creators = channelOption?.data?.creators ?? []

    if (creators.length > 0) {
        return creators.map((c) => {
            const owner = c?.owner ?? c
            return {
                value: getId(owner),
                label: getLabel(owner),
                data:  owner,
                ownerType: getType(owner),
            }
        })
    }

    // Fallback: login user
    if (login) {
        return [{
            value: getId(login),
            label: getLabel(login),
            data:  login,
            ownerType: getType(login) || 'user',
        }]
    }

    return []
}

// ─── Avatar atom ──────────────────────────────────────────────────────────────

const AvatarIcon = ({ src, icon, size = 28, rounded = 'full' }) => (
    <span
        className={classNames(
            'flex items-center justify-center flex-shrink-0 overflow-hidden background-200',
            rounded === 'md' ? 'rounded-md' : 'rounded-full'
        )}
        style={{ width: size, height: size, minWidth: size }}>
        {src
            ? <img src={src} alt="" className="w-full h-full object-cover"
                   onError={(e) => { e.target.style.display = 'none' }} />
            : <span className="text-primary flex items-center justify-center">{icon}</span>
        }
    </span>
)

// ─── Privacy toggle ───────────────────────────────────────────────────────────

const PrivacyToggle = ({ value, onChange }) => {
    const t = useTranslate()
    const opts = [
        { value: PrivacyTypes.PUBLIC,  label: t('public'),  Icon: BiGlobe },
        { value: PrivacyTypes.PRIVATE, label: t('private'), Icon: BiLock  },
    ]
    return (
        <div className="flex items-center border border-color-50 rounded-lg overflow-hidden">
            {opts.map(({ value: v, label, Icon }) => (
                <button
                    key={v}
                    type="button"
                    onClick={() => onChange(v)}
                    className={classNames(
                        'px-3 py-2 flex flex-center gap-2 flex-1 text-sm tracking-wide transition-colors',
                        value === v
                            ? 'bg-secondary-100 font-medium text-secondary'
                            : 'text-color-200 background-100 hover:background-200'
                    )}>
                    <Icon size={14} />
                    <span>{label}</span>
                </button>
            ))}
        </div>
    )
}

// ─── Dropdown ─────────────────────────────────────────────────────────────────

const Dropdown = ({
                      options = [],
                      value,
                      onChange,
                      placeholder,
                      disabled = false,
                      loading  = false,
                      searchable = false,
                      searchValue = '',
                      onSearch,
                      renderOption,
                      renderSelected,
                  }) => {
    const [open, setOpen] = useState(false)
    const wrapRef = useRef(null)

    useEffect(() => {
        const handler = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false)
        }
        document.addEventListener('mousedown', handler)
        return () => document.removeEventListener('mousedown', handler)
    }, [])

    const selected = options.find((o) => o.value === value)

    return (
        <div ref={wrapRef} className="relative w-full">
            <button
                type="button"
                disabled={disabled}
                onClick={() => !disabled && setOpen((v) => !v)}
                className={classNames(
                    'w-full flex items-center justify-between px-3 py-2 rounded-lg border text-sm transition-colors',
                    disabled
                        ? 'border-color-100 background-100 text-color-300 cursor-not-allowed'
                        : 'border-color-200 background hover:border-primary cursor-pointer',
                    open && !disabled && 'border-primary'
                )}>
                <span className="flex-1 min-w-0 text-left">
                    {selected
                        ? (renderSelected ? renderSelected(selected) : <span className="truncate">{selected.label}</span>)
                        : <span className="text-color-300 italic">{placeholder}</span>
                    }
                </span>
                <BiChevronDown
                    size={16}
                    className={classNames(
                        'flex-shrink-0 text-color-400 ml-2 transition-transform duration-150',
                        open && 'rotate-180'
                    )}
                />
            </button>

            {open && (
                <div className="absolute z-50 w-full mt-1 background border border-color-200 rounded-lg shadow-lg overflow-hidden">
                    {searchable && (
                        <div className="p-2 border-b border-color-100">
                            <div className="flex items-center gap-2 px-2 py-1 rounded border border-color-200 background-100">
                                <BiSearch size={13} className="text-color-400 flex-shrink-0" />
                                <input
                                    autoFocus
                                    value={searchValue}
                                    onChange={(e) => {
                                        onSearch && onSearch(e.target.value)
                                    }}
                                    placeholder="Search..."
                                    className="flex-1 bg-transparent text-sm outline-none text-color-700 placeholder-color-300"
                                />
                                {!!searchValue && (
                                    <button type="button" onClick={() => {
                                        onSearch && onSearch('')
                                    }}
                                            className="text-color-300 hover:text-color-500">
                                        <BiX size={13} />
                                    </button>
                                )}
                            </div>
                        </div>
                    )}

                    <div className="overflow-y-auto overscroll-contain" style={{maxHeight: '40vh'}}>
                        {loading ? (
                            <div className="py-6 text-center text-sm text-color-400">Loading...</div>
                        ) : options.length === 0 ? (
                            <div className="py-6 text-center text-sm text-color-400">No results</div>
                        ) : (
                            options.map((opt) => (
                                <button
                                    key={opt.value}
                                    type="button"
                                    onClick={() => { onChange(opt); setOpen(false) }}
                                    className={classNames(
                                        'w-full flex items-center justify-between px-3 py-2 text-sm text-left transition-colors',
                                        opt.value === value ? 'background-100' : 'hover:background-100'
                                    )}>
                                    <span className="flex-1 min-w-0">
                                        {renderOption ? renderOption(opt) : <span className="truncate">{opt.label}</span>}
                                    </span>
                                    {opt.value === value && (
                                        <BiCheck size={15} className="text-primary flex-shrink-0 ml-2" />
                                    )}
                                </button>
                            ))
                        )}
                    </div>
                </div>
            )}
        </div>
    )
}

// ─── Modal ────────────────────────────────────────────────────────────────────

// PostSettingModal nhận toggle từ DrawDialog render prop
// KHÔNG tự làm overlay/bottom sheet — DrawDialog lo responsive đó
const PostSettingModal = ({ defaultSelection, onConfirm, onCancel, toggle }) => {
    const t = useTranslate()
    const login = useContext(LoginContext)
    const { options, isLoading, keyword, setKeyword } = useContext(PostFormContext)

    const [selectedChannelId, setSelectedChannelId] = useState(
        defaultSelection?.container ? getId(defaultSelection.container) : null
    )
    const [selectedCreatorId, setSelectedCreatorId] = useState(
        defaultSelection?.creator ? getId(defaultSelection.creator) : null
    )
    const [privacy, setPrivacy] = useState(defaultSelection?.privacy ?? PrivacyTypes.PUBLIC)

    const selectedChannelOption = useMemo(
        () => options.find((o) => o.value === selectedChannelId) ?? null,
        [options, selectedChannelId]
    )

    const creatorOptions = useMemo(
        () => resolveCreatorOptions(selectedChannelOption, login),
        [selectedChannelOption, login]
    )

    useEffect(() => {
        if (creatorOptions.length === 0) {
            setSelectedCreatorId(null)
            return
        }
        const inList = creatorOptions.find((c) => c.value === selectedCreatorId)
        if (!inList) {
            setSelectedCreatorId(creatorOptions[0].value)
        }
    }, [creatorOptions])

    const renderChannel = useCallback((opt) => (
        <div className="flex items-center gap-2">
            <AvatarIcon size={22} icon={<ChannelIcon size={12} />} />
            <span className="truncate text-sm">{opt.label}</span>
            {opt.is_default === 1 && (
                <span className="text-xs text-primary border border-primary rounded px-1 ml-auto flex-shrink-0">
                    {t('default')}
                </span>
            )}
        </div>
    ), [t])

    const renderCreator = useCallback((opt) => (
        <div className="flex items-center gap-2">
            <AvatarIcon
                size={22}
                src={getAvatar(opt.data)}
                rounded={opt.ownerType === 'organization' ? 'md' : 'full'}
                icon={
                    opt.ownerType === 'organization'
                        ? <BiSolidChurch size={12} />
                        : <RiUserFill size={12} />
                }
            />
            <span className="truncate text-sm">{opt.label}</span>
        </div>
    ), [])

    const handleConfirm = useCallback(() => {
        if (!selectedChannelOption) return
        const creatorOpt = creatorOptions.find((c) => c.value === selectedCreatorId)
        onConfirm({
            container: selectedChannelOption.data,
            creator:   creatorOpt?.data ?? login ?? null,
            privacy,
        })
        // Đóng DrawDialog sau khi confirm
        if (toggle) toggle(false)
    }, [selectedChannelOption, creatorOptions, selectedCreatorId, privacy, login, onConfirm, toggle])

    const handleCancel = useCallback(() => {
        if (onCancel) onCancel()
        if (toggle) toggle(false)
    }, [onCancel, toggle])

    const canConfirm = Boolean(selectedChannelOption)

    // Nội dung thuần — DrawDialog tự wrap thành bottom sheet (mobile) hoặc modal (desktop)
    return (
        <div className="flex flex-col gap-4 p-4">
            {/* Channel */}
            <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-color-200 tracking-wider pr-3">
                    {t('content channel')}
                </label>
                <Dropdown
                    options={options}
                    value={selectedChannelId}
                    loading={isLoading}
                    placeholder={t('select channel...')}
                    searchable
                    searchValue={keyword}
                    onSearch={setKeyword}
                    renderOption={renderChannel}
                    renderSelected={renderChannel}
                    onChange={(opt) => setSelectedChannelId(opt.value)}
                />
            </div>

            {/* Creator */}
            <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-color-200 tracking-wider pr-3">
                    {t('creator')}
                </label>
                <Dropdown
                    options={creatorOptions}
                    value={selectedCreatorId}
                    disabled={!selectedChannelOption}
                    placeholder={t('please select a channel first...')}
                    renderOption={renderCreator}
                    renderSelected={renderCreator}
                    onChange={(opt) => setSelectedCreatorId(opt.value)}
                />
            </div>

            {/* Privacy */}
            <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-color-200 tracking-wider pr-3">
                    {t('privacy')}
                </label>
                <PrivacyToggle value={privacy} onChange={setPrivacy} />
            </div>

            {/* Footer buttons */}
            <div className="flex gap-3 pt-2">
                <button
                    type="button"
                    onClick={handleCancel}
                    style={{
                        padding: '11px 0',
                        borderRadius: 12,
                        border: '1px solid var(--border-color-50, #f0f0f0)',
                        fontSize: 14, fontWeight: 600,
                    }}
                    className="flex-1 background hover:background-50 text-color-200 hover:text-gray-800">
                    {t('cancel')}
                </button>
                <button
                    type="button"
                    disabled={!canConfirm}
                    onClick={handleConfirm}
                    style={{
                        padding: '11px 0',
                        borderRadius: 12,
                        border: 'none',
                        background: canConfirm ? 'var(--color-primary)' : 'var(--background-200, #e0e0e0)',
                        fontSize: 14, fontWeight: 700,
                        color: canConfirm ? '#fff' : 'var(--color-text-400)',
                        cursor: canConfirm ? 'pointer' : 'not-allowed',
                        transition: 'all .15s',
                    }}
                    className="flex-1">
                    {t('confirm')}
                </button>
            </div>
        </div>
    )
}

// ─── Summary label ────────────────────────────────────────────────────────────

const PostSettingSummary = ({ current, onClick, disabled = false }) => {
    const t = useTranslate()
    const { container, creator, privacy } = current ?? {}

    // owner: đối tượng hiển thị ra ngoài — có thể là user, org, hoặc gì khác
    // Ưu tiên: creator.data (Formik shape) → creator trực tiếp
    const owner = creator?.data ?? creator ?? null
    const ownerType = getType(owner)
    const ownerAvatar = getAvatar(owner)
    // getTitle xử lý đúng cho mọi loại entity: user, org, channel...
    const ownerLabel = owner ? getTitle(owner) : null

    // Empty state
    if (!container) {
        return (
            <button
                type="button"
                onClick={onClick}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    width: '100%',
                    padding: '8px 0',
                    background: 'none',
                    border: 'none',
                    cursor: disabled ? 'default' : 'pointer',
                    color: 'var(--color-text-400, #aaa)',
                }}>
                <div style={{
                    width: 38, height: 38, borderRadius: '50%',
                    background: 'var(--background-100, #f0f0f0)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                }}>
                    <BiSearch size={16} />
                </div>
                <span style={{fontSize: 14, fontStyle: 'italic'}}>
                    {t('select channel to post...')}
                </span>
            </button>
        )
    }

    return (
        <button
            type="button"
            onClick={disabled ? undefined : onClick}
            style={{
                padding: 12, textAlign: 'left',
                cursor: disabled ? 'default' : 'pointer',
            }}
            className="w-full flex items-center gap-3 bg-slate-50 border border-slate-100 hover:border-slate-200 rounded-xl">
            {/* Avatar channel */}
            <div
                style={{width: 40, height: 40,}}
                className="bg-secondary-100 text-secondary-600 rounded-full flex flex-center flex-shrink-0">
                <RiSignalTowerFill size={18} />
            </div>
            {/* Info */}
            <div className="flex flex-col flex-1">
                {/* Channel name + privacy pill */}
                <div className="flex items-center gap-x-1">
                    <div className="font-bold text-sm tracking-wide max-lines-1">
                        {getLabel(container)}
                    </div>
                    <span
                        style={{padding: '1px 6px'}}
                        className="w-fit border border-color-100 background hover:border-secondary-500 rounded-full flex items-center gap-2 flex-shrink-0">
                        <span style={{
                            width: 6, height: 6, borderRadius: 999,
                            background: privacy === PrivacyTypes.PRIVATE ? 'var(--color-warning, #f59e0b)' : '#1d9bf0',
                            flexShrink: 0,
                        }} className="flex-shrink-0"/>
                        <span style={{fontSize: 11}} className="font-medium text-slate-500 tracking-wide">
                            {privacy === PrivacyTypes.PRIVATE ? t('private') : t('public')}
                        </span>
                        {/* Chỉ hiện chevron khi không disabled */}
                        {!disabled && (
                            <BsChevronDown size={9} className="text-color-400 flex-shrink-0"/>
                        )}
                    </span>
                </div>
                {/* Owner sub-label — dùng owner (entity hiển thị), không phải creator (người thao tác) */}
                {ownerLabel && (
                    <div className="flex items-center gap-1">
                        <span className="text-xs text-color-400 tracking-wider">
                            {`${t('posting as')}: `}
                        </span>
                        <AvatarIcon
                            size={13}
                            src={ownerAvatar}
                            rounded={ownerType === 'organization' ? 'md' : 'full'}
                            icon={
                                ownerType === 'organization'
                                    ? <BiSolidChurch size={7} />
                                    : <RiUserFill size={7} />
                            }
                        />
                        <span className="font-medium text-xs text-slate-500 tracking-wide truncate whitespace-no-wrap">
                            {ownerLabel}
                        </span>
                    </div>
                )}
            </div>
        </button>
    )
}

// ─── Main export ──────────────────────────────────────────────────────────────

/**
 * PostSettingForm
 *
 * Reads/writes PostFormContext.current.
 * Place inside any component wrapped by <PostFormProvider>.
 * No props needed.
 */

// FormikAwareWrapper — tries to render children (which use useFormikContext).
// If Formik context is not available, renders fallback instead.
// Uses a class-based ErrorBoundary since hooks can't catch render errors.
class FormikAwareWrapper extends React.Component {
    constructor(props) {
        super(props)
        this.state = { hasError: false }
    }
    static getDerivedStateFromError() {
        return { hasError: true }
    }
    render() {
        if (this.state.hasError) {
            return this.props.fallback ?? null
        }
        return this.props.children
    }
}

// Inner component — only rendered when inside a Formik context.
// Safely calls useFormikContext and syncs values on confirm.
const PostSettingFormWithFormik = ({ onConfirm, disabled = false }) => {
    const { setFieldValue } = useFormikContext()

    const { options } = useContext(PostFormContext)

    const handleConfirm = useCallback(
        (selection) => {
            const { container, creator, privacy } = selection ?? {}

            const channelOpt = options.find((o) => o.value === getId(container))
            if (channelOpt) {
                setFieldValue('container', channelOpt)
            }
            if (creator) {
                setFieldValue('owner', {
                    value: getId(creator),
                    data:  creator,
                    id:    getId(creator),
                    type:  getType(creator),
                    label: creator?.name ?? creator?.username ?? '',
                })
            }
            if (privacy !== undefined) {
                setFieldValue('privacy', privacy)
            }

            onConfirm(selection)
        },
        [options, setFieldValue, onConfirm]
    )

    return <PostSettingFormInner onConfirm={handleConfirm} disabled={disabled} />
}

// Base inner component — dùng DrawDialog thay vì tự quản lý open state
// DrawDialog tự handle: mobile = bottom sheet, desktop = modal
// disabled=true: chỉ hiện summary, không cho mở dialog (dùng khi edit bài)
const PostSettingFormInner = ({ onConfirm, disabled = false }) => {
    const t = useTranslate()
    const { current } = useContext(PostFormContext)

    // Khi disabled — chỉ hiện summary read-only, không wrap DrawDialog
    if (disabled) {
        return (
            <div style={{width: '100%', cursor: 'default', opacity: 0.85}}>
                <PostSettingSummary
                    current={current}
                    onClick={() => {}}
                    disabled={true}
                />
            </div>
        )
    }

    return (
        <DrawDialog
            width={480}
            title={
                <div className="font-bold text-color-000 uppercase tracking-wide text-sm">
                    {t('post settings')}
                </div>
            }
            closable={true}
            component={
                // Trigger — PostSettingSummary đóng vai trò nút mở
                <div style={{width: '100%', cursor: 'pointer'}}>
                    <PostSettingSummary current={current} onClick={() => {}}/>
                </div>
            }>
            {(isToggle, toggle) => isToggle && (
                <PostSettingModal
                    defaultSelection={current}
                    onConfirm={(selection) => {
                        onConfirm(selection)
                    }}
                    onCancel={() => toggle(false)}
                    toggle={toggle}
                />
            )}
        </DrawDialog>
    )
}

const PostSettingForm = ({ disabled = false }) => {
    const { current, setCurrent } = useContext(PostFormContext)

    const handleConfirm = useCallback(
        (selection) => { setCurrent(selection) },
        [setCurrent]
    )

    return (
        <FormikAwareWrapper
            fallback={<PostSettingFormInner onConfirm={handleConfirm} disabled={disabled} />}>
            <PostSettingFormWithFormik onConfirm={handleConfirm} disabled={disabled} />
        </FormikAwareWrapper>
    )
}

export default PostSettingForm